import { useState } from "react";
import { useLocation } from "react-router-dom";
import Icon, { FacebookIcon } from "../components/Icon";
import { COMPANY, telHref } from "../content/site";
import useDocumentTitle from "../lib/useDocumentTitle";
import f from "../components/forms.module.css";
import s from "./Contact.module.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact({ addRequest }) {
  useDocumentTitle("Kontakt");
  const prefilled = useLocation().state?.message || "";
  const [form, setForm] = useState({ name: "", email: "", phone: "+420", message: prefilled });
  const [touched, setTouched] = useState({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const touch = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const phoneEmpty = !form.phone.trim() || form.phone.trim() === "+420";
  const errors = {
    name: !form.name.trim() && "Vyplňte jméno.",
    email: !EMAIL_RE.test(form.email) && "Zadejte platný e-mail, např. jana@example.com",
    phone: !phoneEmpty && !(/^\+\d{1,4}( \d{3}){3}$/.test(form.phone) || /^\+\d{10,13}$/.test(form.phone)) && "Formát +420 123 456 789",
    message: !form.message.trim() && "Napište nám, s čím můžeme pomoci.",
  };

  async function submit(e) {
    e.preventDefault();
    setTouched({ name: true, email: true, phone: true, message: true });
    if (Object.values(errors).some(Boolean)) return;
    setSending(true);
    setError("");
    try {
      await addRequest({
        source: "contact", id: Date.now(), submittedAt: new Date().toISOString(),
        name: form.name.trim(), email: form.email.trim(), gsm: phoneEmpty ? "" : form.phone, message: form.message.trim(),
        status: "New", adminNote: "",
      });
      setSent(true);
    } catch (_) {
      setError(`Zprávu se nepodařilo odeslat. Zkuste to prosím znovu, nebo nám zavolejte na ${COMPANY.phone}.`);
    } finally {
      setSending(false);
    }
  }

  const fieldError = (k) => touched[k] && errors[k];

  return (
    <div className="container">
      <header className={s.head}>
        <p className="eyebrow">Kontakt</p>
        <h1 className="h1">Ozvěte se nám</h1>
        <p className="lead">Dotazy, poptávky, prohlídky i testovací jízdy. Odpovídáme do jednoho pracovního dne.</p>
      </header>

      <div className={s.layout}>
        <div className={f.card}>
          {sent ? (
            <div className={f.success} role="status">
              <Icon name="check-circle" size={52} strokeWidth={1.5} />
              <h2 className="h2">Zpráva odeslána</h2>
              <p className="muted">Děkujeme. Ozveme se vám do jednoho pracovního dne.</p>
            </div>
          ) : (
            <form onSubmit={submit} noValidate>
              {prefilled && (
                <div className={`${f.alert} ${f.alertInfo}`}><Icon name="car" size={18} />Zprávu jsme předvyplnili podle vozu, o který máte zájem.</div>
              )}
              <div className={f.row}>
                <label className={f.field}>
                  <span className={f.label}>Jméno a příjmení</span>
                  <input type="text" name="name" autoComplete="name" value={form.name} onChange={set("name")} onBlur={touch("name")}
                    aria-invalid={!!fieldError("name")} aria-describedby="c-name-err" />
                  {fieldError("name") && <span id="c-name-err" className={f.error}><Icon name="alert-circle" size={16} />{errors.name}</span>}
                </label>
                <label className={f.field}>
                  <span className={f.label}>E-mail</span>
                  <input type="email" name="email" autoComplete="email" inputMode="email" value={form.email} onChange={set("email")} onBlur={touch("email")}
                    aria-invalid={!!fieldError("email")} aria-describedby="c-email-err" />
                  {fieldError("email") && <span id="c-email-err" className={f.error}><Icon name="alert-circle" size={16} />{errors.email}</span>}
                </label>
              </div>
              <label className={f.field}>
                <span className={f.label}>Telefon<span className={f.optional}>nepovinné</span></span>
                <input type="tel" name="tel" autoComplete="tel" inputMode="tel" value={form.phone} onChange={set("phone")} onBlur={touch("phone")}
                  aria-invalid={!!fieldError("phone")} aria-describedby="c-tel-err" />
                {fieldError("phone") && <span id="c-tel-err" className={f.error}><Icon name="alert-circle" size={16} />{errors.phone}</span>}
              </label>
              <label className={f.field}>
                <span className={f.label}>Zpráva</span>
                <textarea rows={6} value={form.message} onChange={set("message")} onBlur={touch("message")} placeholder="Zajímám se o…"
                  aria-invalid={!!fieldError("message")} aria-describedby="c-msg-err" />
                {fieldError("message") && <span id="c-msg-err" className={f.error}><Icon name="alert-circle" size={16} />{errors.message}</span>}
              </label>
              {error && <div className={`${f.alert} ${f.alertError}`} role="alert"><Icon name="alert-circle" size={18} />{error}</div>}
              <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={sending}>
                {sending ? "Odesílám…" : <>Odeslat zprávu <Icon name="arrow-right" size={18} /></>}
              </button>
            </form>
          )}
        </div>

        <aside className={s.info} aria-label="Kontaktní údaje">
          <a href={telHref(COMPANY.phone)} className={s.item}>
            <Icon name="phone" size={20} />
            <span><span className={s.label}>Telefon</span><span className={`${s.value} num`}>{COMPANY.phone}</span></span>
          </a>
          <a href={`mailto:${COMPANY.email}`} className={s.item}>
            <Icon name="mail" size={20} />
            <span><span className={s.label}>E-mail</span><span className={s.value}>{COMPANY.email}</span></span>
          </a>
          <div className={s.item}>
            <Icon name="clock" size={20} />
            <span><span className={s.label}>Otevírací doba</span><span className={s.value}>{COMPANY.hours}</span></span>
          </div>
          <div className={s.item}>
            <Icon name="map-pin" size={20} />
            <span>
              <span className={s.label}>Adresa</span>
              <span className={s.value}>{COMPANY.owner}<br />{COMPANY.street}<br />{COMPANY.city}</span>
            </span>
          </div>
          <div className={s.item}>
            <Icon name="file-text" size={20} />
            <span><span className={s.label}>IČO</span><span className={`${s.value} num`}>{COMPANY.ico}</span></span>
          </div>
          <a href={COMPANY.facebook} target="_blank" rel="noopener noreferrer" className={s.item}>
            <FacebookIcon size={20} />
            <span><span className={s.label}>Facebook</span><span className={s.value}>VoltNaPřání</span></span>
          </a>
        </aside>
      </div>
    </div>
  );
}
